import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useQCStandardQuery, useUpdateQCStandard } from "../api/queries";
import { QCFormTour, useQCFormTour } from "../tour/QCFormTour";
import { QCForm } from "../ui/QCForm";

export function QCEditPage() {
  const { id } = useParams({ from: "/app/qc/$id/edit" });
  const nav = useNavigate({ from: "/app/qc/$id/edit" });
  const tour = useQCFormTour();

  const qc = useQCStandardQuery(id);
  const updateQC = useUpdateQCStandard(id);

  if (qc.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (qc.error || !qc.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-destructive">ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ</p>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/qc" })}
              className="mt-4"
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const qcData = qc.data;

  return (
    <>
      <Header />
      <Main>
        <QCFormTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">ແກ້ໄຂມາດຕະຖານ QC</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ແກ້ໄຂຂໍ້ມູນ: {qcData.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  nav({
                    to: "/app/qc/$id",
                    params: { id },
                  });
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          {/* Edit Form Section */}
          <div className="rounded-xl border bg-card p-6">
            <QCForm
              initialValues={
                qcData
                  ? {
                      name: qcData.name,
                      description: qcData.description ?? undefined,
                      workTypeId: qcData.workTypeId,
                      workStage: qcData.workStage ?? undefined,
                      departmentId: qcData.departmentId ?? undefined,
                      inspectorPositionId:
                        qcData.inspectorPositionId ?? undefined,
                      inspectorUserIds:
                        qcData.inspectors?.map((i) => i.userId) ?? [],
                      status: qcData.status,
                      requirePhoto: qcData.requirePhoto,
                      requireNotesOnFail: qcData.requireNotesOnFail,
                      passCriteria: qcData.passCriteria ?? undefined,
                      failCriteria: qcData.failCriteria ?? undefined,
                      actionOnFail: qcData.actionOnFail ?? undefined,
                      notifyPositionId: qcData.notifyPositionId ?? undefined,
                      checklistId: qcData.checklistId ?? undefined,
                      kpiId: qcData.kpiId ?? undefined,
                      items:
                        qcData.items?.map(
                          (item: {
                            id: string;
                            title: string;
                            description: string | null;
                            orderIndex: number;
                            isRequired: boolean;
                            passCriteria: string | null;
                            failCriteria: string | null;
                          }) => ({
                            id: item.id,
                            title: item.title,
                            description: item.description ?? null,
                            orderIndex: item.orderIndex,
                            isRequired: item.isRequired,
                            passCriteria: item.passCriteria ?? null,
                            failCriteria: item.failCriteria ?? null,
                          }),
                        ) ?? [],
                    }
                  : undefined
              }
              onSubmit={async (vals) => {
                await updateQC.mutateAsync({
                  name: vals.name,
                  description: vals.description ?? undefined,
                  workTypeId: vals.workTypeId,
                  workStage: vals.workStage ?? undefined,
                  departmentId: vals.departmentId ?? undefined,
                  inspectorPositionId: vals.inspectorPositionId ?? undefined,
                  inspectorUserIds: vals.inspectorUserIds ?? [],
                  status: vals.status,
                  requirePhoto: vals.requirePhoto,
                  requireNotesOnFail: vals.requireNotesOnFail,
                  passCriteria: vals.passCriteria ?? undefined,
                  failCriteria: vals.failCriteria ?? undefined,
                  actionOnFail: vals.actionOnFail ?? undefined,
                  notifyPositionId: vals.notifyPositionId ?? undefined,
                  checklistId: vals.checklistId ?? undefined,
                  kpiId: vals.kpiId ?? undefined,
                  items: vals.items?.map((item, index) => ({
                    ...item,
                    orderIndex: item.orderIndex ?? index,
                  })),
                });
                nav({
                  to: "/app/qc/$id",
                  params: { id },
                });
              }}
              submitting={updateQC.isPending}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
